import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Radio, Lock } from "lucide-react";

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Login | Donald Inbody";
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#C5A059] flex items-center justify-center">
            <Radio size={20} className="text-[#0A1628]" />
          </div>
          <span className="font-serif text-[#F5F0E8] font-bold text-sm uppercase tracking-wider">
            Donald Inbody
          </span>
        </div>

        <div className="bg-[#F5F0E8] p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock size={16} className="text-[#0A1628]" />
            <div>
              <h1 data-testid="admin-login-title" className="font-serif text-xl font-bold text-[#0A1628]">
                Admin Access
              </h1>
              <p className="text-[#2D3142]/60 text-xs">Blog management portal</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="w-full border border-[#0A1628]/20 bg-white px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#0A1628]"
                  placeholder="admin@donaldinbody.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="w-full border border-[#0A1628]/20 bg-white px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#0A1628]"
                  placeholder="••••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-red-600 text-sm mb-4 bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-[#0A1628] text-[#F5F0E8] py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>

        <p className="text-center text-[#F5F0E8]/30 text-xs mt-6">
          Restricted access — authorized personnel only
        </p>
      </div>
    </div>
  );
}
